import { api, ApiError, formatBps, formatMoney } from '@bemor/web-kit';
import {
    DecimalInput,
    Field,
    FormSheet,
    MoneyInput,
    ReasonDialog,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    statusLabel,
    Textarea,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { RiskMeasure, RiskMeasureRequest } from '@/api/types';
import { useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

/** The reads a measure write moves: the monitoring list and detail, the measures, the approvals queue. */
export const MEASURE_INVALIDATES = [
    '/staff/risk/monitoring',
    '/staff/risk/measures',
    '/staff/approvals',
    '/staff/queues',
];

/** A measure in words: "Volume cap of $5,000.00 a day", "Suspension (payments)", "Extra reserve at 25.00%". */
export function measureLabel(m: {
    kind: string;
    scope?: string | null;
    mode?: string | null;
    params?: Record<string, unknown>;
}): string {
    const kind =
        (
            ADMIN_STATUSES.riskMeasureKind as Record<
                string,
                readonly [string, string]
            >
        )[m.kind]?.[1] ?? statusLabel(m.kind);
    const p = (m.params ?? {}) as Record<string, number | undefined>;
    if (m.kind === 'suspension')
        return `${kind} (${m.scope === 'account' ? 'whole account' : 'payments only'})`;
    if (m.kind === 'extra_reserve')
        return m.mode === 'rate'
            ? `${kind} at ${formatBps(p.reserve_bps ?? 0)} of new sales`
            : `${kind} of ${formatMoney(p.amount_cents ?? 0, 'USD')} on the balance`;
    if (m.kind === 'volume_cap')
        return `${kind} of ${formatMoney(p.cap_cents ?? 0, 'USD')} a day`;
    return kind;
}

const KINDS = [
    [
        'capture_hold',
        'Capture hold',
        'Every new authorization waits in Review for a staff decision.',
    ],
    [
        'extra_reserve',
        'Extra reserve',
        'A higher reserve rate on new sales, or a fixed hold on the balance.',
    ],
    [
        'volume_cap',
        'Volume cap',
        'Payments over the daily cap go to Review for a staff decision. The proposer may approve it.',
    ],
    [
        'suspension',
        'Suspension',
        'Payments only (sign-in and orders keep working), or the whole account.',
    ],
] as const;

/**
 * BM-125 decisions 30, 31, 33: propose a measure on the merchant's monitoring case. It goes to Approvals; a second
 * member approves it, except a volume cap, which its proposer may approve. One Idempotency-Key per sheet.
 */
export function ProposeMeasureSheet({
    merchantId,
    onClose,
    onProposed,
}: {
    merchantId: string;
    onClose: () => void;
    onProposed: (m: RiskMeasure) => void;
}) {
    const [kind, setKind] = useState<RiskMeasureRequest['kind']>('volume_cap');
    const [scope, setScope] = useState<'payments' | 'account'>('payments');
    const [mode, setMode] = useState<'rate' | 'amount'>('rate');
    const [cents, setCents] = useState<number | null>(null);
    const [bps, setBps] = useState<number | null>(null);
    const [reason, setReason] = useState('');
    const [key, setKey] = useState(() => crypto.randomUUID());
    const [error, setError] = useState<unknown>(null);
    const write = useWrite(
        async (body: RiskMeasureRequest) =>
            (
                await api().post<RiskMeasure>(
                    `/staff/risk/monitoring/${merchantId}/measures`,
                    body,
                    { idempotencyKey: key },
                )
            ).data,
        MEASURE_INVALIDATES,
    );
    const needsCents =
        kind === 'volume_cap' ||
        (kind === 'extra_reserve' && mode === 'amount');
    const needsBps = kind === 'extra_reserve' && mode === 'rate';
    const ready =
        reason.trim() !== '' &&
        (!needsCents || (cents ?? 0) > 0) &&
        (!needsBps || (bps ?? 0) > 0);
    const body = (): RiskMeasureRequest => ({
        kind,
        reason: reason.trim(),
        ...(kind === 'suspension' ? { scope } : {}),
        ...(kind === 'extra_reserve' ? { mode } : {}),
        ...(kind === 'volume_cap' ? { params: { cap_cents: cents ?? 0 } } : {}),
        ...(needsCents && kind === 'extra_reserve'
            ? { params: { amount_cents: cents ?? 0 } }
            : {}),
        ...(needsBps ? { params: { reserve_bps: bps ?? 0 } } : {}),
    });
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title="Propose a measure"
            description="The proposal opens or joins this merchant's monitoring case and goes to Approvals."
            submitLabel="Send to Approvals"
            busy={write.isPending}
            submitDisabled={!ready}
            dirty
            error={error}
            data-testid="propose-measure"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(body(), {
                    onSuccess: (m) => onProposed(m),
                    onError: (err) => {
                        setError(err);
                        // an HTTP answer ends this key; a lost response keeps it for the resubmit
                        if (ApiError.is(err)) setKey(crypto.randomUUID());
                    },
                });
            }}
        >
            <Field
                id="msr-kind"
                label="Measure"
                hint={KINDS.find(([k]) => k === kind)?.[2]}
            >
                <Select
                    value={kind}
                    onValueChange={(v) =>
                        setKind(v as RiskMeasureRequest['kind'])
                    }
                >
                    <SelectTrigger id="msr-kind">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {KINDS.map(([k, label]) => (
                            <SelectItem key={k} value={k}>
                                {label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            {kind === 'suspension' ? (
                <Field
                    id="msr-scope"
                    label="Scope"
                    hint="The approver sees the scope. Lifting an account suspension approves the merchant again."
                >
                    <Select
                        value={scope}
                        onValueChange={(v) =>
                            setScope(v as 'payments' | 'account')
                        }
                    >
                        <SelectTrigger id="msr-scope">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="payments">
                                Payments only
                            </SelectItem>
                            <SelectItem value="account">
                                Whole account
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
            ) : null}
            {kind === 'extra_reserve' ? (
                <Field id="msr-mode" label="Reserve">
                    <Select
                        value={mode}
                        onValueChange={(v) => setMode(v as 'rate' | 'amount')}
                    >
                        <SelectTrigger id="msr-mode">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="rate">
                                Higher rate on new sales (released after 180
                                days)
                            </SelectItem>
                            <SelectItem value="amount">
                                Fixed hold on the balance
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
            ) : null}
            {needsCents ? (
                <Field
                    id="msr-cents"
                    label={
                        kind === 'volume_cap' ? 'Daily cap' : 'Amount to hold'
                    }
                >
                    <MoneyInput
                        id="msr-cents"
                        cents={cents}
                        onChange={setCents}
                    />
                </Field>
            ) : null}
            {needsBps ? (
                <Field
                    id="msr-bps"
                    label="Reserve rate (%)"
                    hint="The share of each new sale held in reserve"
                >
                    <DecimalInput
                        id="msr-bps"
                        suffix="%"
                        value={bps}
                        onValueChange={setBps}
                    />
                </Field>
            ) : null}
            <Field id="msr-reason" label="Reason">
                <Textarea
                    id="msr-reason"
                    maxLength={1000}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </Field>
        </FormSheet>
    );
}

/** Lift an applied measure (the automatic payout pause too, decision 29) or withdraw a proposal, with a reason. */
export function LiftMeasureDialog({
    measure,
    onClose,
}: {
    measure: RiskMeasure;
    onClose: () => void;
}) {
    const [key, setKey] = useState(() => crypto.randomUUID());
    const lift = useWrite(
        async (reason: string) =>
            (
                await api().post<RiskMeasure>(
                    `/staff/risk/measures/${measure.id}/lift`,
                    { reason },
                    { idempotencyKey: key },
                )
            ).data,
        MEASURE_INVALIDATES,
    );
    const applied = measure.state === 'applied';
    return (
        <ReasonDialog
            open
            onOpenChange={(o) => (o ? null : onClose())}
            title={
                applied
                    ? `Lift the ${measureLabel(measure).toLowerCase()}`
                    : 'Withdraw this proposal'
            }
            description={
                measure.kind === 'payout_pause'
                    ? 'The merchant is paid again from the next payout run. The case keeps the note.'
                    : applied
                      ? 'The measure ends now and the case keeps the note.'
                      : 'The proposal is withdrawn; its approval no longer applies anything.'
            }
            confirmLabel={applied ? 'Lift' : 'Withdraw'}
            required
            busy={lift.isPending}
            error={lift.error}
            onConfirm={(reason) =>
                lift.mutate(reason, {
                    onSuccess: onClose,
                    onError: (err) => {
                        if (ApiError.is(err)) setKey(crypto.randomUUID());
                    },
                })
            }
        />
    );
}
